import { createContext, useCallback, useContext, useMemo, useState } from 'react'

const FeedbackContext = createContext(null)

const DEFAULT_CONFIRM = {
  isOpen: false,
  title: 'Please confirm',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  tone: 'danger',
  resolve: null,
}

function createToastId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export function FeedbackProvider({ children }) {
  const [confirmState, setConfirmState] = useState(DEFAULT_CONFIRM)
  const [toasts, setToasts] = useState([])

  const closeConfirm = useCallback((result) => {
    setConfirmState((previousState) => {
      if (previousState.resolve) {
        previousState.resolve(Boolean(result))
      }
      return DEFAULT_CONFIRM
    })
  }, [])

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title: options?.title || 'Please confirm',
        message: options?.message || '',
        confirmLabel: options?.confirmLabel || 'Confirm',
        cancelLabel: options?.cancelLabel || 'Cancel',
        tone: options?.tone || 'danger',
        resolve,
      })
    })
  }, [])

  const toast = useCallback((options) => {
    const id = createToastId()
    const durationMs = Number(options?.durationMs) > 0 ? Number(options.durationMs) : 3200
    const nextToast = {
      id,
      type: options?.type || 'info',
      message: options?.message || '',
    }
    setToasts((previous) => [...previous, nextToast])
    window.setTimeout(() => {
      setToasts((previous) => previous.filter((item) => item.id !== id))
    }, durationMs)
  }, [])

  const value = useMemo(
    () => ({
      confirm,
      toast,
    }),
    [confirm, toast],
  )

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      {confirmState.isOpen ? (
        <div className="modalOverlay" role="presentation" onClick={() => closeConfirm(false)}>
          <div
            className="modalCard"
            role="dialog"
            aria-modal="true"
            aria-label={confirmState.title}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="modalTitle">{confirmState.title}</h3>
            <p className="modalDescription">{confirmState.message}</p>
            <div className="modalActions">
              <button type="button" className="button buttonGhost" onClick={() => closeConfirm(false)}>
                {confirmState.cancelLabel}
              </button>
              <button
                type="button"
                className={`button ${confirmState.tone === 'danger' ? 'buttonDanger' : 'buttonSecondary'}`}
                onClick={() => closeConfirm(true)}
              >
                {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="toastStack" aria-live="polite" aria-atomic="true">
        {toasts.map((item) => (
          <div
            key={item.id}
            className={`toast ${item.type === 'error' ? 'toastError' : item.type === 'success' ? 'toastSuccess' : 'toastInfo'}`}
          >
            {item.message}
          </div>
        ))}
      </div>
    </FeedbackContext.Provider>
  )
}

export function useFeedback() {
  const context = useContext(FeedbackContext)
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider')
  }
  return context
}
